/**
 * Строка «Голоса клиента» (#848): «X — в K из N разговоров», пометка правила
 * Р3 (выросло, упало, новое, исчезло — #2394 §9 п. 6), спарклайн доли по окну
 * (п. 7) и раскрытие до ряда и разговоров, по одной формулировке с каждого, свежие
 * первыми. Меньше пяти разговоров — словами «пока догадка», а не выводом.
 *
 * Формулировка — пересказ анализа (контекст упоминания, отзыв, сигнал), а не
 * дословная цитата, поэтому переход в разговор её не подсвечивает: подсветка
 * пообещала бы, что клиент сказал именно это.
 *
 * Строки вопросов клиентов (#843) показывают последние формулировки и не
 * раскрываясь (`preview`); формулировку, изъятую редакцией, — словами
 * «формулировка скрыта»: вопрос был.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn, formatDateShort } from '@/lib/utils';
import { VoiceRowDetails } from './VoiceRowDetails';
import type { VoiceRowDetailsTrend } from './voice-series-view-model';
import { VoiceSparkline } from './VoiceSparkline';
import { seriesHasPoints } from './voice-series-view-model';
import {
  isSmallSample,
  itemText,
  rowCountsText,
  rowLabel,
  rowMark,
  VOICE_NO_MARK,
  type VoiceRow,
} from './voice-view-model';
import { themeBaselineText } from './voice-themes-view-model';
import { useVoiceComparable } from './voice-theme-filter-context';

interface VoiceRowCardProps {
  row: VoiceRow;
  expanded: boolean;
  onToggle: () => void;
  onOpenCall: (recordId: string) => void;
  /** The row the page is filtered by (#2229): framed as current. */
  highlighted?: boolean;
  /** Сколько последних формулировок видно у свёрнутой строки; нет — ни одной. */
  preview?: number;
  /**
   * Ряд строки по окну (#2394 §9 п. 7): спарклайн у чисел и график в раскрытии.
   * Нет — ни того, ни другого; без точек за окно — спарклайна нет (почему,
   * говорит строка блока), а раскрытие говорит это и держит «12 месяцев».
   */
  trend?: VoiceRowDetailsTrend;
  /** Блок строки — в виде «Что изменилось», где строки разных блоков вместе. */
  blockLabel?: string;
}

export function VoiceRowCard({
  row,
  expanded,
  onToggle,
  onOpenCall,
  preview = 0,
  highlighted = false,
  trend,
  blockLabel,
}: VoiceRowCardProps) {
  const { t } = useTranslation('dashboard');
  // Under a theme filter whose previous window is not comparable (#2229): no
  // change at all, the filter strip says why.
  const comparable = useVoiceComparable();
  const mark = comparable ? rowMark(row) : VOICE_NO_MARK;
  const previewItems = expanded ? [] : row.items.slice(0, preview);
  // Unique per card: a key stripped to ASCII made every Cyrillic competitor
  // the same id, and `aria-controls` pointed at another row (code-review #1208).
  const detailsId = `voice-${useId()}-details`;
  // The line needs a point; the open row's «12 months» does not — a window
  // without data (before the series began) still has the row's history.
  const sparkTrend = trend && seriesHasPoints(trend.series) ? trend : undefined;

  return (
    <div
      className={cn(
        // A container: the sparkline hides by the card's width (#2394 §6).
        '@container rounded-card border bg-bg-surface',
        highlighted ? 'border-primary' : 'border-border'
      )}
      aria-current={highlighted ? 'true' : undefined}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={expanded}
        aria-controls={detailsId}
        onClick={onToggle}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-fg-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-fg-muted" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
          {rowLabel(row, t)}
          {blockLabel && (
            <span className="ml-2 text-xs font-normal text-fg-muted">{blockLabel}</span>
          )}
        </span>
        {row.competitor && !row.competitor.merged && (
          <span title={t('voicePage.competitors.unmergedHint')}>
            <Badge variant="default">{t('voicePage.competitors.unmerged')}</Badge>
          </span>
        )}
        {/* Seen without opening the row: a share over a handful of
            conversations is a guess, and the collapsed list is what gets read
            (#1379). The full sentence stays in the details. */}
        {isSmallSample(row.calls) && (
          <span title={t('voicePage.smallSample')}>
            <Badge variant="default">{t('voicePage.smallSampleBadge')}</Badge>
          </span>
        )}
        {mark.kind === 'new' && <Badge variant="info">{t('voicePage.trend.new')}</Badge>}
        {mark.kind === 'gone' && (
          <span title={t('voicePage.trend.goneHint')}>
            <Badge variant="default">{t('voicePage.trend.gone')}</Badge>
          </span>
        )}
        {(mark.kind === 'up' || mark.kind === 'down') && (
          <span
            className="font-mono text-xs text-fg-muted"
            title={t('voicePage.trend.prevShare', { share: mark.prevShare })}
          >
            {t(mark.kind === 'up' ? 'voicePage.trend.up' : 'voicePage.trend.down', {
              delta: Math.abs(mark.delta),
            })}
          </span>
        )}
        {/* A theme whose previous window is not comparable names its base
            instead of an arrow: a changed vocabulary is not a change in calls. */}
        {row.theme?.baseline && (
          <span className="text-xs text-fg-muted">
            {themeBaselineText(row.theme.baseline, t, formatDateShort)}
          </span>
        )}
        {sparkTrend && (
          <VoiceSparkline series={sparkTrend.series} unit={sparkTrend.unit} />
        )}
        <span
          className={cn(
            'font-mono text-xs',
            row.calls > 0 ? 'text-fg-muted' : 'text-fg-subtle'
          )}
        >
          {rowCountsText(row, t)}
        </span>
      </button>

      {previewItems.length > 0 && (
        <ul className="space-y-1 border-t border-border px-3 py-2">
          {previewItems.map((item) => (
            <li
              key={`${item.recordId}-${item.text}`}
              className={cn(
                'truncate text-xs',
                item.text ? 'text-fg-muted' : 'text-fg-subtle'
              )}
            >
              {item.text ? `«${item.text}»` : itemText(item, t)}
            </li>
          ))}
        </ul>
      )}

      {expanded && (
        <VoiceRowDetails id={detailsId} row={row} onOpenCall={onOpenCall} trend={trend} />
      )}
    </div>
  );
}
